<template>
	<div class="post-comment" v-bind:class="{
		'mt-4': !children,
		'post-comment--parent' : !children && comment.answers_count,
		'pt-4 pl-16 post-comment--children' : children
	}">
		<div
			class="d-flex"
			:class="{
				'post-comment-parent-branch' : !children && comment.answers_count
			}"
		>
			<div class="mr-6">
				<content-hover avatar :user="comment.user" />
			</div>
			<div class="flex-grow-1">
				<div class="grey--text grey lighten-1 text--darken-2 py-3 px-3 rounded post-comment-content">
					<div class="d-flex justify-content-between mb-1">
						<div>
							<content-hover :user="comment.user" hover-class="bold"/>
							<div @click="goToProfile()" class="font-14 font-weight-bold" :style="{cursor: 'pointer'}">
							</div>
							<div v-if="comment.user.job_position" class="font-12 grey--text">
								{{ comment.user.job_position }}
							</div>
						</div>
						<div>
							<post-dropdown
								v-if="dropdownOptions.findIndex(o => o.display) >= 0"
								:options="dropdownOptions"
							/>
						</div>
					</div>
					<template v-if="!edition">
						<!--					<div class="font-14 pt-3">-->
						<!--						<q><span>@User User</span> Duis aute irure dolor in reprehenderit in voluptate velit</q>-->
						<!--						Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.-->
						<!--					</div>-->
						<multicontent :quote="comment.quote" :content="comment.content" :mentions="comment.mention_users" small url-preview/>
					</template>
					<template v-else>
						<div class="d-flex">
							<content-editable-input
								v-model="commentContent"
								emojis
								ref="content-editable-input"
								@enterPress="editCommentListener"
								@escapePress="edition = false"
								class="d-flex flex-grow-1"
							/>
							<div class="ml-2">
								<v-btn depressed color="primary" class="h-100" @click="editCommentListener" :loading="saving.editing">
									{{ $vuetify.lang.t('$vuetify.views.wall.post.update') }}
								</v-btn>
							</div>
						</div>
					</template>
				</div>
				<div class="d-flex flex-wrap w-100 align-items-center">
					<div v-if="comment.reactions_count" class="d-flex align-center">
						<post-reaction-icons :comment="comment" />
						<v-divider vertical inset class="ml-3"></v-divider>
					</div>
					<template v-if="!postDeleted">
						<div v-for="(button, i) in buttons" v-bind:key="'post-comment-button-' + i" class="mr-1">
							<post-comment-button
								v-if="button.display"
								:id="(button.type === 'reaction') ? 'post-reaction-button-' + _uid : null"
								:text="button.type === 'reaction' ? getReactionText() : button.text"
								:icon="button.icon"
								:action="button.action"
								:highlighted="button.color"
								:sending="sending"
								:type="button.type"
								@mouseenter.native="(button.type === 'reaction') ? $refs['post-reaction-buttons'].show() : null"
								@mouseleave.native="(button.type === 'reaction') ? $refs['post-reaction-buttons'].hide() : null"
							/>
						</div>
						<post-reaction-add
							ref="post-reaction-buttons"
							:attach="'post-reaction-button-' + _uid"
							:comment="comment"
							:nudge-top="40"
						/>
					</template>
					<div class="font-12 grey--text ml-auto">
						{{ $moment(comment.created_at).fromNow() }}
					</div>
				</div>
				<post-comment-add
					v-model="commentAdded"
					v-if="showAnswerAdd && !post.deleted_by_user_at"
					class="mt-2"
					:post="post"
					:parent="comment.id"
					ref="post-comment-add"
				/>
			</div>
		</div>
		<post-comment-list
			:comments="comment.answers"
			:comments-count="comment.answers_count"
			:post="post"
			:parent="comment.id"
			children
			:postDeleted="postDeleted"
		/>
		<post-comment-remove-dialog
			:visible="showCommentRemoveDialog"
			:action="removeCommentCall"
			:comment="comment"
			:saving="saving.removing"
		/>
		<report-abuse-dialog :comment="comment" v-if="showReportAbuse" @closed="(showReportAbuse = false)" />
	</div>
</template>

<script>
import PostCommentButton from "@/components/wall/post/PostCommentButton";
import PostDropdown from "@/components/wall/post/PostDropdown";
import {mapActions, mapGetters} from "vuex";
import PostCommentAdd from "@/components/wall/post/PostCommentAdd";
import ContentEditableInput from "@/components/items/ContentEditableInput";
import { decodeMentions } from "@/functions/mentions";
import PostCommentRemoveDialog from "@/components/wall/post/PostCommentRemoveDialog";
import Multicontent from '@/components/items/Multicontent';
import PostReactionAdd from "@/components/wall/post/PostReactionAdd";
import PostReactionIcons from "@/components/wall/post/PostReactionIcons";
const ReportAbuseDialog = import("@/components/wall/dialogs/ReportAbuseDialog");
import ContentHover from "@/components/items/ContentHover";
export default {
	name: "PostCommentShow",
	components: {
		PostCommentList: () => import('@/components/wall/post/PostCommentList'),
		PostCommentButton,
		PostDropdown,
		PostCommentAdd,
		ContentEditableInput,
		Multicontent,
		PostCommentRemoveDialog,
		PostReactionAdd,
		PostReactionIcons,
		ReportAbuseDialog: () => ({
			component: ReportAbuseDialog,
		}),
		ContentHover
	},
	props: {
		post: {
			type: Number,
			required: true
		},
		parent: {
			type: Number,
			default: null
		},
		comment: {
			type: Object,
			required: true
		},
		children: {
			type: Boolean,
			default: false
		},
		userReaction: {
			type: Object,
			default: null
		},
		postDeleted: {
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			commentAdded: false,
			saving: {
				editing: false,
				removing: false
			},
			showReportAbuse: false,
			sending: null,
			commentContent: null,
			edition: false,
			showAnswerAdd: false,
			showCommentRemoveDialog: false,
			quote: null,
			buttons: [
				{
					icon: '$like-small',
					text: '',
					display: true,
					type: 'reaction',
					color: false,
					action: async () => {
						if (this.sending)
							return null
						let type = 'like'
						this.sending = 'reaction'
						if (this.userReaction) {
							let id = this.userReaction.id,
								reactionID = this.userReaction.id
							if (await this.removeReaction({ id }))
								this.$store.commit('wall/removeReactionFromComment',{ comment: this.comment, reactionID })
						} else {
							let reactionable_type = 'comment',
								reactionable_id = this.comment.id,
								reaction = await this.setReaction({ type, reactionable_type, reactionable_id })
							console.log(this.comment)
							this.$store.commit('wall/appendReactionToComment',{ comment: this.comment, reaction })
						}
						this.sending = null
					}
				},
				{
					icon: '$message',
					text: 'Odpowiedz',
					display: true,
					type: 'message',
					color: 'grey--text',
					action: () => {
						this.showAnswerAdd = !this.showAnswerAdd
					}
				},
			],
			dropdownOptions: [
				{
					value: 0,
					text: this.$vuetify.lang.t('$vuetify.views.wall.post.comment.actions.remove'),
					icon: '$message-off',
					type: 'remove',
					display: false,
					action: () => {
						this.showCommentRemoveDialog = true
					}
				},
				{
					value: 0,
					text: this.$vuetify.lang.t('$vuetify.views.wall.post.comment.actions.edit'),
					icon: '$edit',
					type: 'edit',
					display: false,
					action: async () => {
						this.commentContent = {
							content: this.comment.content,
							mention_users: this.comment.mention_users
						}
						this.edition = true
					}
				},
				{
					value: 0,
					text: this.$vuetify.lang.t('$vuetify.views.wall.post.comment.actions.edit'),
					icon: '$edit',
					type: 'edit',
					display: false,
					action: async () => {
						this.commentContent = {
							content: this.comment.content,
							mention_users: this.comment.mention_users
						}
						this.edition = true
					}
				},
				{
					value: 0,
					text: this.$vuetify.lang.t('$vuetify.views.wall.post.actions.reportAbuse'),
					icon: '$exclamation',
					type: 'abuse',
					display: false,
					action: () => {
						this.showReportAbuse = true;
					}
				},
			]
		}
	},
	computed: {
		...mapGetters({
			user: 'auth/user'
		}),
	},
	watch: {
		userReaction: function () {
			this.checkUserReaction()
		},
		'commentAdded': function (v) {
			if (v) {
				this.showAnswerAdd = false
				this.commentAdded = false
			}

		},
		'comment.deleted_by_user_at': function () {
			this.checkDeleteState()
			this.checkEditAbility()
		}
	},
	created() {
		this.checkUserReaction()
		this.checkEditAbility()
		this.checkQuote()
		this.checkDeleteState()
		this.checkAbuseReportAbility()
	},
	methods: {
		...mapActions('wall', [
			'removeComment',
			'editComment',
			'setReaction',
			'updateReaction',
			'removeReaction',
			'setQuotedCommentContent'
		]),
		getReactionText() {
			if(this.comment && this.comment.my_reaction) {
				return this.$vuetify.lang.t('$vuetify.views.wall.post.buttons.reacted.' + this.comment.my_reaction.type)
			}
			return this.$vuetify.lang.t('$vuetify.views.wall.post.buttons.reaction')
		},
		checkAbuseReportAbility() {
			let idx = this.dropdownOptions.findIndex(o => o.type === 'abuse');
			if(idx != -1) {
				if(this.user.id != this.comment.user.id && !this.comment.deleted_by_user_at) {
					this.dropdownOptions[idx].display = true;
				}
				else {
					this.dropdownOptions[idx].display = false;
				}
			}
		},
		checkDeleteState() {
			let deleted = this.comment.deleted_by_user_at && this.comment.deleted_by_user_at.length > 0
			for (let i in this.buttons) {
				this.buttons[i].display = !deleted
			}
		},
		checkQuote() {
			let comment = Object.assign(this.comment, {
				post: {
					id: this.post
				}
			})
			this.setQuotedCommentContent({ comment })
		},
		checkUserReaction() {
			let index = this.buttons.findIndex(b => b.type === 'reaction')
			if (index >= 0) {
				let data = {
					color: 'grey--text',
					text: 'Poleć',
					icon: '$like-small',
				}

				if (this.userReaction) {
					data = this.getUserReactionTextAndColor(this.userReaction.type)
				}

				this.buttons[index] = Object.assign(this.buttons[index], data)
			}

		},
		getUserReactionTextAndColor(name) {
			switch (name) {
				case 'applause':
					return {
						color: 'blue--text',
						text: 'Polecasz',
						icon: '$applause-reaction',
					}
				case "happy":
					return {
						color: 'gold--text',
						text: 'Polecasz',
						icon: '$face-reaction',
					}
				case "love":
					return {
						color: 'warning--text',
						text: 'Polecasz',
						icon: '$heart-reaction',
					}
				default:
					return {
						color: 'success--text',
						text: 'Polecasz',
						icon: '$like-small',
					}
			}
		},
		checkEditAbility() {
			if (this.user.id === this.comment.user.id || (this.user.role && this.user.role.id === 2)) {
				let editIndex = (this.user.role && this.user.role.id === 2 && this.user.id !== this.comment.user.id) ? -1 : this.dropdownOptions.findIndex(o => o.type === 'edit'),
					removeIndex = this.dropdownOptions.findIndex(o => o.type === 'remove'),
					deleted = this.comment.deleted_by_user_at && this.comment.deleted_by_user_at.length > 0

				if (editIndex >= 0)
					this.dropdownOptions[editIndex].display = !deleted
				this.dropdownOptions[removeIndex].display = !deleted
			}
		},
		checkMentions(val, users) {
			return decodeMentions(val, users)
		},
		goToProfile() {
			this.$router
				.push({
					name: "Profile",
					params: { userID: this.comment.user.id },
				})
				.catch(() => {});
		},
		async editCommentListener() {
			this.saving.editing = true;
			let comment = {
				id: this.comment.id,
				content: this.commentContent.content,
				mention_user_ids: this.commentContent.mentions,
				post: {
					id: this.post
				},
				parent_id: this.parent,
				answers: this.comment.answers
			}

			await this.editComment({ comment})
			this.saving.editing = false;
			this.edition = false
		},
		async removeCommentCall(remove, deleted_as) {
			this.showCommentRemoveDialog = false
			if (!remove)
				return null
			let comment = Object.assign(this.comment, {
					post: {
						id: this.post
					},
					parent_id: this.parent
				}),
				params = {
					deleted_as: deleted_as
				}
			this.saving.removing = true;
			await this.removeComment({ comment, params })
			this.saving.removing = false;
		}
	}
}
</script>

<style lang="scss">
$post-comment-branch-left: 20px;
.post-comment {
	&--parent {
		.post-comment-parent-branch {
			position: relative;
			&::before {
				position: absolute;
				content: "";
				left: $post-comment-branch-left;
				bottom: 0;
				width: 1px;
				height: calc(100% - 50px);
				background-color: var(--v-grey-lighten3);
			}
		}
	}
	&--children {
		position: relative;
		&:not(:last-child) {
			&::before {
				height: 100%;
			}
		}
		&:last-child {
			&::before {
				height: 16px;
			}
		}
		&::before {
			position: absolute;
			content: "";
			left: $post-comment-branch-left;
			top: 0;
			width: 1px;
			background-color: var(--v-grey-lighten3);
		}
		&::after {
			position: absolute;
			content: "";
			left: $post-comment-branch-left;
			top: 16px;
			width: 30px;
			height: 20px;
			border-left: 1px solid;
			border-bottom: 1px solid;
			border-radius: 0 0 0 10px;
			border-color: var(--v-grey-lighten3);
		}
	}
	.post-comment-content {
		position: relative;
		&::before {
			width: 0;
			height: 0;
			border-style: solid;
			border-width: 12px 11px 12px 0;
			border-color: transparent var(--v-grey-lighten1) transparent transparent;
			position: absolute;
			top: 8px;
			left: -8px;
			content: "";
		}
		q {
			display: block;
			background-color: var(--v-grey-lighten2);
			color: var(--v-grey-darken2);
			padding: 6px 8px;
			border-radius: 4px;
			font-size: 12px;
			line-height: 16px;
			font-style: italic;
			&::before, &::after {
				display: none;
			}
			margin-bottom: 10px;
			&:not(:first-child) {
				margin-top: 10px;
			}
			span {
				color: var(--v-primary-base);
				font-weight: 700;
			}
		}
	}
}
</style>
